import React, { Fragment, ReactNode } from 'react';

type ParagraphBlock = { type: 'paragraph'; text: string };
type ListBlock = { type: 'ordered-list' | 'unordered-list'; items: string[] };
type AnswerBlock = ParagraphBlock | ListBlock;
type AnswerListBlockType = ListBlock['type'];

const ORDERED_LIST_ITEM_REGEX = /^\d+\.\s+(.*)$/;
const UNORDERED_LIST_ITEM_REGEX = /^[-*]\s+(.*)$/;
const BOLD_SEGMENT_REGEX = /(\*\*.+?\*\*)/g;
const BOLD_TEXT_REGEX = /^\*\*(.+?)\*\*$/;

const flushParagraph = (blocks: AnswerBlock[], lines: string[]) => {
  const text = lines.join('\n').trim();
  if (text) {
    blocks.push({ type: 'paragraph', text });
  }
};

const flushList = (
  blocks: AnswerBlock[],
  type: AnswerListBlockType | null,
  items: string[],
) => {
  if (type && items.length) {
    blocks.push({ type, items: [...items] });
  }
};

export const parseAnswerBlocks = (answer: string): AnswerBlock[] => {
  const normalizedAnswer = (answer || '').replace(/\r\n?/g, '\n').trim();

  if (!normalizedAnswer) {
    return [];
  }

  const blocks: AnswerBlock[] = [];
  const paragraphLines: string[] = [];
  let activeListType: AnswerListBlockType | null = null;
  let activeListItems: string[] = [];

  normalizedAnswer.split('\n').forEach((rawLine) => {
    const line = rawLine.trim();

    if (!line) {
      flushParagraph(blocks, paragraphLines);
      flushList(blocks, activeListType, activeListItems);
      paragraphLines.length = 0;
      activeListType = null;
      activeListItems = [];
      return;
    }

    const orderedMatch = line.match(ORDERED_LIST_ITEM_REGEX);
    if (orderedMatch) {
      flushParagraph(blocks, paragraphLines);
      paragraphLines.length = 0;

      if (activeListType !== 'ordered-list') {
        flushList(blocks, activeListType, activeListItems);
        activeListType = 'ordered-list';
        activeListItems = [];
      }

      activeListItems.push(orderedMatch[1]);
      return;
    }

    const unorderedMatch = line.match(UNORDERED_LIST_ITEM_REGEX);
    if (unorderedMatch) {
      flushParagraph(blocks, paragraphLines);
      paragraphLines.length = 0;

      if (activeListType !== 'unordered-list') {
        flushList(blocks, activeListType, activeListItems);
        activeListType = 'unordered-list';
        activeListItems = [];
      }

      activeListItems.push(unorderedMatch[1]);
      return;
    }

    flushList(blocks, activeListType, activeListItems);
    activeListType = null;
    activeListItems = [];
    paragraphLines.push(line);
  });

  flushParagraph(blocks, paragraphLines);
  flushList(blocks, activeListType, activeListItems);

  return blocks;
};

const renderInlineFormatting = (text: string): ReactNode[] => text
  .split(BOLD_SEGMENT_REGEX)
  .filter(Boolean)
  .map((segment, index) => {
    const boldMatch = segment.match(BOLD_TEXT_REGEX);

    if (boldMatch) {
      return <strong key={`segment-${index}`}>{boldMatch[1]}</strong>;
    }

    return <Fragment key={`segment-${index}`}>{segment}</Fragment>;
  });

const renderFormattedText = (text: string): ReactNode[] => text
  .split('\n')
  .reduce<ReactNode[]>((nodes, line, lineIndex) => {
    if (lineIndex > 0) {
      nodes.push(<br key={`break-${lineIndex}`} />);
    }

    nodes.push(
      <Fragment key={`line-${lineIndex}`}>
        {renderInlineFormatting(line)}
      </Fragment>,
    );

    return nodes;
  }, []);

interface FormattedAnswerProps {
  answer: string;
}

const FormattedAnswer = ({ answer }: FormattedAnswerProps) => {
  const blocks = parseAnswerBlocks(answer);

  if (!blocks.length) {
    return null;
  }

  return (
    <div className='answer'>
      {blocks.map((block, blockIndex) => {
        if (block.type === 'ordered-list') {
          return (
            <ol key={`block-${blockIndex}`} className='answer-list answer-list-ordered'>
              {block.items.map((item, itemIndex) => (
                <li key={`ordered-item-${itemIndex}`} className='answer-list-item'>
                  {renderFormattedText(item)}
                </li>
              ))}
            </ol>
          );
        }

        if (block.type === 'unordered-list') {
          return (
            <ul key={`block-${blockIndex}`} className='answer-list answer-list-unordered'>
              {block.items.map((item, itemIndex) => (
                <li key={`unordered-item-${itemIndex}`} className='answer-list-item'>
                  {renderFormattedText(item)}
                </li>
              ))}
            </ul>
          );
        }

        if (block.type === 'paragraph') {
          return (
            <p key={`block-${blockIndex}`} className='answer-paragraph'>
              {renderFormattedText(block.text)}
            </p>
          );
        }

        return null;
      })}
    </div>
  );
};

export default FormattedAnswer;
